/**
 * HomePage Component
 * Landing page with hero section, featured drops, and challenges
 * Extracted from App.js for better maintainability
 */
import React, { useState, useEffect, useMemo, useRef, Component } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { getPackUnit, getHobbySeriesLabel } from "../../utils/packUnit";
import { toast } from "sonner";
import {
  Package, Clock, Play, ChevronRight, Gift, Trophy,
  Shield, Sparkles, Star, Calendar, Check, CreditCard,
  Store, TrendingUp, Users, Copy, Share2, Lock, Diamond, Zap, Search, Coins, Activity,
  Volume2, VolumeX, Camera, RefreshCw, Smartphone
} from "lucide-react";
import { API, getShareOrigin, navigateToLogin } from "../../utils";
import { getMediaUrl } from "../../utils.js";
import HERO_FALLBACK_DATAURI from "../../assets/heroFallback";
import LiveBreakHero from "../home/LiveBreakHero";
import ResponsiveImage from "../common/ResponsiveImage";
import SwipeCarousel from "../common/SwipeCarousel";
import usePerformanceMode from "../../hooks/usePerformanceMode";
import ARPromoScene from "../ar/ARPromoScene";
import ARViewer from "../ar/ARViewer";

const CDN_URL = process.env.REACT_APP_CDN_URL || '';

// Import extracted components
import VideoMomentCard from "../collectibles/VideoMomentCard";
import HoloCard from "../collectibles/HoloCard";
import BLKCardFrame from "../collectibles/BLKCardFrame";
import EmergeBadge from "../common/EmergeBadge";
import HighlightedText from "../common/HighlightedText";
import ImageCarousel from "../common/ImageCarousel";
import PushNotificationPrompt from "../common/PushNotificationPrompt";
import AutoPlayVideo from "../common/AutoPlayVideo";
import { CardSkeleton } from "../common/Skeletons";
// RunItWinsTicker removed 2026-06-02 — bot wins banner pulled off Home.
import FirstRipRotationTicker from "../common/FirstRipRotationTicker";
// FeaturedTournamentBanner + LiveMatchesWidget removed 2026-06-02 — replaced by LiveBreakHero below Upcoming Drops.
import DropPoolPreview from "../common/DropPoolPreview";
import { SPORT_OPTIONS, classifyDrop } from "../../utils/sportClassifier";
// PackRevealHero removed — hero section is now "OWN THE MOMENTS"

// ============================================================================
// ============================================================================
// UpcomingDropsCarousel — homepage upcoming-drops grid.
//
// On mobile renders as an Instagram Reels-style horizontal snap-scroller via
// the shared <SwipeCarousel>; on sm+ degrades to the original responsive grid.
//
// May 28, 2026 rewrite — the previous bespoke implementation had two bugs:
//   1) Pager dots never updated as you swiped, because each child was wrapped
//      in <AnimatedSection> (a plain <div>) so el.children[i] no longer had
//      the data-drop-tile marker the scroll-tracker filtered on. Result: dot
//      tracking returned [] → activeIdx stuck at 0 forever.
//   2) Rendered one pager dot per drop with no cap — 9-10 drops produced 9-10
//      dots, which looked terrible on mobile and made tap-targets tiny.
//
// Both are fixed by switching to SwipeCarousel (proven on /play) AND capping
// the mobile carousel to MAX_HOMEPAGE_CAROUSEL drops. Full grid on sm+ still
// shows everything; the "View All" link sends mobile users to /play for the
// rest. Each tile keeps its fade-up entrance animation via renderItem.
// ============================================================================
// Cap the MOBILE carousel only — desktop grid shows ALL drops.
// The mobile cap pairs with MAX_DOTS=5 inside SwipeCarousel so the pager
// dot strip never exceeds 5 dots on phones. Desktop has no cap because
// a real CSS grid handles 50 drops just as well as 5 — and any cap there
// produces dead empty cells (the bug that prompted this rewrite:
// 5 items in a 4-col grid = 4 + 1 + 3 empty slots).
const MAX_HOMEPAGE_CAROUSEL = 5;

// Single drop tile — used by both the mobile carousel and the desktop
// grid so they stay visually identical card-for-card.
const HomeDropTile = ({ drop, idx, navigate }) => (
  <AnimatedSection key={drop.id} animation="fade-up" delay={idx * 100}>
    <div
      className="cursor-pointer group"
      onClick={() => navigate(`/drops/${drop.id}`)}
      data-testid={`home-drop-card-${drop.id}`}
    >
      <BLKCardFrame
        imageUrl={drop.card_images?.[0] || drop.image_url || drop.home_display_image}
        videoUrl={drop.video_url}
        name={drop.name}
        rarity={drop.drop_rarity}
        series={getHobbySeriesLabel(drop)}
        isAR={drop.is_ar_drop}
        isHobby={drop.is_hobby_drop}
        aspectRatio="4/5"
        dropId={drop.id}
        badge={
          <>
            {drop.is_hobby_drop && <span className="brand-gradient-bg text-black text-[10px] font-black px-2 py-0.5 rounded shadow-[0_0_8px_rgba(249,115,22,0.5)]">HOBBY · +5 PTS</span>}
            {drop.is_preorder && <span className="bg-amber-500/90 text-black text-[10px] font-bold px-2 py-0.5 rounded">PRE-ORDER</span>}
            {drop.is_pool_drop && <span className="bg-amber-500/90 text-black text-[10px] font-bold px-2 py-0.5 rounded">POOL</span>}
          </>
        }
        verificationId={drop.verification_id}
      />
      <div className="mt-3 px-1.5">
        <h3 className="text-white font-black text-base sm:text-lg uppercase leading-tight truncate tracking-tight" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>{drop.name}</h3>
        <div className="flex justify-between items-center mt-1.5">
          <span className="text-gray-400 text-xs sm:text-sm font-medium">{drop.packs_remaining} {getPackUnit(drop).plural_for(drop.packs_remaining)} left</span>
          <span className="text-white font-black text-sm sm:text-base">{drop.price} pts</span>
        </div>
        {Array.isArray(drop.pool_cards) && drop.pool_cards.length >= 2 && (
          <div className="mt-3">
            <DropPoolPreview cards={drop.pool_cards} variant="compact" isHobbyDrop={!!drop.is_hobby_drop} />
          </div>
        )}
      </div>
    </div>
  </AnimatedSection>
);

const UpcomingDropsCarousel = ({ drops, navigate }) => {
  const carouselDrops = drops.slice(0, MAX_HOMEPAGE_CAROUSEL);
  return (
    <>
      {/* MOBILE — Reels-style horizontal snap-scroll, capped at 5 for clean dots */}
      <div className="sm:hidden">
        <SwipeCarousel
          items={carouselDrops}
          gridCols="2-3-4"
          swipeHintKey="home-upcoming"
          testId="upcoming-drops-carousel"
          renderItem={(drop, idx) => (
            <HomeDropTile drop={drop} idx={idx} navigate={navigate} />
          )}
        />
      </div>
      {/* DESKTOP / TABLET — full responsive grid, no cap.
          Grid columns mirror SwipeCarousel's "2-3-4" preset so the visual
          density matches what users see on /play. Renders every drop the
          backend returned — no empty cells, no truncation. */}
      <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
        {drops.map((drop, idx) => (
          <HomeDropTile key={drop.id} drop={drop} idx={idx} navigate={navigate} />
        ))}
      </div>
    </>
  );
};
// ============================================================================

// ============================================
// Simple pass-through wrappers (no animations, no sticky, no delays)
const AnimatedSection = ({ children, className = '' }) => <div className={className}>{children}</div>;
const StickyScrollSection = ({ children, className = '', bgColor = '' }) => <div className={`relative w-full ${bgColor} ${className}`}>{children}</div>;
// ============================================

// Section Error Boundary - wraps each homepage section so one crash doesn't blank the whole page
class SectionErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error(`[SectionErrorBoundary] ${this.props.name || 'unknown'} crashed:`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return null; // silently skip broken section
    }
    return this.props.children;
  }
}

// Error Boundary for 3D Cube - prevents crashes from breaking the whole page
class CubeErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('RotatingCube error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-[200px] h-[200px] md:w-[280px] md:h-[280px] bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl flex items-center justify-center border border-orange-500/30">
          <div className="text-center">
            <Package className="text-amber-300 mx-auto mb-2" size={48} />
            <p className="text-amber-300 font-bold text-sm">BLK PACKS™</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Auth context

// ClaimDigitalBanner component
const ClaimDigitalBanner = ({ onClaimClick }) => (
  <div className="bg-[#12121e] journey-glow rounded-xl p-6 mb-8">
    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-white/5 border border-white/[0.08] flex items-center justify-center">
          <Package className="text-white" size={24} />
        </div>
        <div>
          <h3 className="text-white font-bold text-lg">Have a Physical BLKPacks Card?</h3>
          <p className="text-gray-500 text-sm">Claim your matching digital collectible now!</p>
        </div>
      </div>
      <button
        onClick={onClaimClick}
        className="bg-white hover:bg-gray-200 text-black font-bold px-6 py-3 transition-all flex items-center gap-2"
      >
        <Gift size={18} />
        Claim Physical Card
      </button>
    </div>
  </div>
);

// ReferralSection component - Friends & Family Program (Compact Version)
const ReferralSection = ({ user, token }) => {
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user && token) {
      fetchReferralData();
    } else {
      setLoading(false);
    }
  }, [user, token]);

  const fetchReferralData = async () => {
    try {
      const res = await axios.get(`${API}/referrals/my-code`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralData(res.data);
    } catch (err) {
      console.error("Failed to fetch referral data:", err);
    }
    setLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareReferral = () => {
    const shareUrl = `${getShareOrigin()}?ref=${referralData?.referral_code}`;
    const shareText = "Join BLKPacks - Collect authentic graded collectibles with digital companions! Use my referral code and we both earn bonus points when you make a purchase! 🏆";
    
    if (navigator.share) {
      navigator.share({
        title: 'Join BLKPacks',
        text: shareText,
        url: shareUrl
      });
    } else {
      copyToClipboard(shareUrl);
    }
  };

  return (
    <section className="py-4 px-4 bg-[#12121e]" data-testid="referral-section">
      <div className="max-w-4xl mx-auto">
        <div className="relative journey-glow bg-gradient-to-r from-orange-900/80 via-orange-800/80 to-orange-900/80 rounded-xl p-4 overflow-hidden">
          {/* Background effects - more subtle */}
          <div className="absolute inset-0 pointer-events-none opacity-50">
            <div className="absolute top-0 left-1/4 w-32 h-32 bg-orange-500/20 rounded-full blur-[60px]"></div>
            <div className="absolute bottom-0 right-1/4 w-32 h-32 bg-amber-400/10 rounded-full blur-[60px]"></div>
          </div>
          
          <div className="relative">
            {user && referralData ? (
              /* Logged in user - compact layout */
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                {/* Left: Title + Stats */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="text-amber-400" size={18} />
                    <h3 className="text-white font-bold text-base">Friends & Family</h3>
                    <span className="text-[10px] bg-orange-500 text-black px-1.5 py-0.5 rounded font-bold">+5 PTS</span>
                  </div>
                  
                  {/* Inline Stats */}
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5">
                      <span className="text-amber-400 font-bold">{referralData.successful_referrals}</span>
                      <span className="text-green-200/60 text-xs">completed</span>
                    </div>
                    <div className="w-px h-4 bg-orange-500/30"></div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-amber-300 font-bold">{referralData.pending_referrals}</span>
                      <span className="text-green-200/60 text-xs">pending</span>
                    </div>
                    <div className="w-px h-4 bg-orange-500/30"></div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-white font-bold">{referralData.total_bonus_earned.toFixed(0)} pts</span>
                      <span className="text-green-200/60 text-xs">earned</span>
                    </div>
                    {referralData.is_referral_champion && (
                      <>
                        <div className="w-px h-4 bg-orange-500/30"></div>
                        <span className="text-amber-300 text-xs font-bold flex items-center gap-1">🏆 Champion</span>
                      </>
                    )}
                  </div>
                  
                  {/* Progress to Champion - compact */}
                  {!referralData.is_referral_champion && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-[#12121e]/40 rounded-full overflow-hidden max-w-[150px]">
                        <div 
                          className="h-full bg-gradient-to-r from-orange-500 to-amber-400"
                          style={{ width: `${Math.min(100, (referralData.successful_referrals / 20) * 100)}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-green-200/60">{referralData.referrals_until_champion} to 🏆</span>
                    </div>
                  )}
                </div>
                
                {/* Right: Code + Share */}
                <div className="flex items-center gap-2">
                  <div className="bg-[#12121e]/40 rounded-lg px-3 py-2 border border-orange-500/30 flex items-center gap-2">
                    <code className="text-sm font-mono font-bold text-amber-400 tracking-wider">{referralData.referral_code}</code>
                    <button
                      onClick={() => copyToClipboard(referralData.referral_code)}
                      className="p-1 hover:bg-orange-500/20 rounded transition-colors"
                      data-testid="copy-referral-code-btn"
                    >
                      {copied ? <Check className="text-amber-400" size={14} /> : <Copy className="text-amber-300" size={14} />}
                    </button>
                  </div>
                  <button
                    onClick={shareReferral}
                    className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-black text-sm font-bold rounded-lg hover:from-amber-400 hover:to-orange-500 transition-all flex items-center gap-1.5 shadow-lg shadow-orange-500/20"
                    data-testid="share-referral-btn"
                  >
                    <Share2 size={14} />
                    Share
                  </button>
                </div>
              </div>
            ) : (
              /* Not logged in - single row CTA */
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Users className="text-amber-400" size={18} />
                  <div>
                    <h3 className="text-white font-bold text-base">Friends & Family</h3>
                    <p className="text-green-200/70 text-xs">Invite friends, both earn 5 bonus points!</p>
                  </div>
                </div>
                <button
                  onClick={() => navigateToLogin(navigate)}
                  className="px-5 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-black text-sm font-bold rounded-lg hover:from-amber-400 hover:to-orange-500 transition-all"
                  data-testid="referral-signin-btn"
                >
                  Sign In to Get Your Code
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// SecurityPurity component - Premium Enhanced Version with Animations
const SecurityPurity = () => (
  <section className="py-4 md:py-6 px-4 bg-[#12121e] relative overflow-hidden">
    {/* Animated Background Effects */}
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-[150px] animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gray-500/10 rounded-full blur-[150px] animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-orange-500/5 rounded-full blur-3xl"></div>
    </div>
    
    <div className="max-w-5xl mx-auto relative z-10">
      {/* CERTIFIED AUTHENTIC Badge - Enhanced */}
      <div className="text-center mb-4">
        <div className="relative inline-block">
          <div className="absolute inset-0 bg-orange-500/20 rounded-full blur-xl animate-pulse"></div>
          <div className="relative inline-flex items-center gap-2 px-5 py-2.5 border border-orange-500/50 rounded-full bg-[#12121e]/80 backdrop-blur-sm">
            <Shield className="text-amber-400 animate-pulse" size={18} />
            <span className="text-amber-400 text-xs font-bold tracking-[0.2em]">CERTIFIED AUTHENTIC</span>
          </div>
        </div>
      </div>
      
      {/* Section Header - Animated */}
      <div className="text-center mb-4">
        <span className="text-amber-400/60 text-xs uppercase tracking-[0.3em] font-bold">AUTHENTICITY GUARANTEED</span>
        <h2 className="text-3xl md:text-5xl font-black mt-2">
          <span className="text-white">Quality & </span>
          <span className="brand-gradient-text animate-gradient-x">Certification</span>
        </h2>
        <p className="text-gray-400 mt-4 max-w-xl mx-auto text-sm md:text-base leading-relaxed">
          <HighlightedText text="Every BLK PACKS collectible is professionally graded with industry-leading anti-counterfeiting technology. Each card ships with a Verification ID, Card ID, and Serial Number backed by a Certificate of Authenticity." />
        </p>
      </div>
      
      {/* Gold & Silver Cards - Premium with Glow */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* GOLD Card */}
        <div className="group relative">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/30 to-transparent rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
          <div className="relative bg-[#12121e]/60 backdrop-blur-sm card-spectrum card-spectrum-legendary rounded-2xl p-6 transition-all duration-300 hover:transform hover:scale-[1.02]">
            {/* Tier badge */}
            <div className="absolute -top-3 right-4 bg-orange-500 text-white text-xs font-black px-3 py-1 rounded-full">
              GOLD
            </div>
            
            <div className="flex items-center gap-4">
              {/* GLD Badge with glow */}
              <div className="relative">
                <div className="absolute inset-0 bg-orange-500/40 rounded-xl blur-lg group-hover:blur-xl transition-all"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/40 group-hover:scale-110 transition-transform">
                  <span className="text-black font-black text-lg">GLD</span>
                </div>
              </div>
              
              {/* Info */}
              <div className="flex-1">
                <h3 className="text-amber-400 font-black text-2xl tracking-wide">GOLD BLK PACK</h3>
                <p className="text-amber-400/60 text-sm">Premium Tier • A+ Grade</p>
              </div>
              
              {/* Grade with animation */}
              <div className="text-right">
                <div className="text-white font-black font-title text-3xl md:text-4xl">
                  A<span className="text-amber-400 text-lg">+</span>
                </div>
                <div className="text-amber-400/50 text-xs uppercase tracking-wider">GRADE</div>
              </div>
            </div>
            
            {/* Standard text */}
            <div className="mt-5 pt-4 border-t border-orange-500/20 flex items-center gap-2">
              <div className="w-5 h-5 bg-orange-500/20 rounded-full flex items-center justify-center">
                <Check className="text-orange-500" size={12} />
              </div>
              <span className="text-gray-300 text-sm">Professionally graded collectible</span>
            </div>
          </div>
        </div>
        
        {/* SILVER Card */}
        <div className="group relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-400/30 to-transparent rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
          <div className="relative bg-[#12121e]/60 backdrop-blur-sm border-2 border-gray-500/40 rounded-2xl p-6 hover:border-gray-400/80 transition-all duration-300 hover:transform hover:scale-[1.02]">
            {/* Tier badge */}
            <div className="absolute -top-3 right-4 bg-gray-400 text-black text-xs font-black px-3 py-1 rounded-full">
              SILVER
            </div>
            
            <div className="flex items-center gap-4">
              {/* SLV Badge with glow */}
              <div className="relative">
                <div className="absolute inset-0 bg-gray-400/40 rounded-xl blur-lg group-hover:blur-xl transition-all"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-gray-300 to-gray-500 rounded-xl flex items-center justify-center shadow-lg shadow-gray-500/40 group-hover:scale-110 transition-transform">
                  <span className="text-gray-800 font-black text-lg">SLV</span>
                </div>
              </div>
              
              {/* Info */}
              <div className="flex-1">
                <h3 className="text-gray-300 font-black text-2xl tracking-wide">SILVER BLK PACK</h3>
                <p className="text-gray-500 text-sm">Classic Tier • A Grade</p>
              </div>
              
              {/* Grade */}
              <div className="text-right">
                <div className="text-white font-black text-3xl md:text-4xl">
                  A<span className="text-gray-400 text-lg"></span>
                </div>
                <div className="text-gray-500 text-xs uppercase tracking-wider">GRADE</div>
              </div>
            </div>
            
            {/* Standard text */}
            <div className="mt-5 pt-4 border-t border-gray-700 flex items-center gap-2">
              <div className="w-5 h-5 bg-orange-500/20 rounded-full flex items-center justify-center">
                <Check className="text-orange-500" size={12} />
              </div>
              <span className="text-gray-300 text-sm">Professionally graded collectible</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Anti-Counterfeiting Technology Section - Premium */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-amber-500/10 rounded-3xl blur-xl"></div>
        <div className="relative bg-[#12121e]/60 backdrop-blur-sm border border-gray-800 rounded-3xl p-6 md:p-10">
          {/* Header */}
          <div className="text-center mb-6">
            <span className="text-orange-400/60 text-xs uppercase tracking-[0.3em] font-bold">PROTECTION</span>
            <h3 className="text-2xl md:text-3xl font-black mt-2">
              <span className="text-white">Anti-Counterfeiting </span>
              <span className="brand-gradient-text">Technology</span>
            </h3>
          </div>
          
          {/* 4 Feature Icons Grid - Premium Cards - Equal Height */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {/* Tamper-Evident */}
            <div className="group relative h-full">
              <div className="absolute inset-0 bg-orange-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
              <div className="relative bg-[#12121e]/60 backdrop-blur-sm border border-orange-500/30 rounded-2xl p-5 text-center transition-all duration-300 hover:transform hover:scale-105 h-full flex flex-col hover:border-orange-500/60">
                {/* Number badge */}
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-black text-xs font-black">
                  01
                </div>
                <div className="relative mb-3">
                  <div className="absolute inset-0 bg-orange-500/30 rounded-xl blur-lg group-hover:blur-xl transition-all"></div>
                  <div className="relative w-14 h-14 mx-auto bg-gradient-to-br from-orange-500 to-orange-700 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/40">
                    <Shield className="text-white" size={28} />
                  </div>
                </div>
                <h4 className="text-white font-bold text-sm mb-1">Tamper-Evident</h4>
                <p className="text-gray-500 text-xs flex-1">Holographic security seals</p>
              </div>
            </div>
            
            {/* UV Security */}
            <div className="group relative h-full">
              <div className="absolute inset-0 bg-orange-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
              <div className="relative bg-[#12121e]/60 backdrop-blur-sm border border-orange-500/30 rounded-2xl p-5 text-center transition-all duration-300 hover:transform hover:scale-105 h-full flex flex-col hover:border-orange-500/60">
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-black text-xs font-black">
                  02
                </div>
                <div className="relative mb-3">
                  <div className="absolute inset-0 bg-orange-500/30 rounded-xl blur-lg group-hover:blur-xl transition-all"></div>
                  <div className="relative w-14 h-14 mx-auto bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/40">
                    <Sparkles className="text-white" size={28} />
                  </div>
                </div>
                <h4 className="text-white font-bold text-sm mb-1">UV Security</h4>
                <p className="text-gray-500 text-xs flex-1">Invisible light markers</p>
              </div>
            </div>
            
            {/* Micro-Engraving - Yellow/Orange */}
            <div className="group relative h-full">
              <div className="absolute inset-0 bg-orange-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
              <div className="relative bg-[#12121e]/60 backdrop-blur-sm border border-orange-500/30 rounded-2xl p-5 text-center hover:border-orange-500/60 transition-all duration-300 hover:transform hover:scale-105 h-full flex flex-col">
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-black text-xs font-black">
                  03
                </div>
                <div className="relative mb-3">
                  <div className="absolute inset-0 bg-orange-500/30 rounded-xl blur-lg group-hover:blur-xl transition-all"></div>
                  <div className="relative w-14 h-14 mx-auto bg-gradient-to-br from-orange-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/40">
                    <TrendingUp className="text-white" size={28} />
                  </div>
                </div>
                <h4 className="text-white font-bold text-sm mb-1">Micro-Engraving</h4>
                <p className="text-gray-500 text-xs flex-1">Laser precision detail</p>
              </div>
            </div>
            
            {/* Serial Numbered - Green */}
            <div className="group relative h-full">
              <div className="absolute inset-0 bg-orange-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
              <div className="relative bg-[#12121e]/60 backdrop-blur-sm card-spectrum card-spectrum-common rounded-2xl p-5 text-center transition-all duration-300 hover:transform hover:scale-105 h-full flex flex-col">
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-black text-xs font-black">
                  04
                </div>
                <div className="relative mb-3">
                  <div className="absolute inset-0 bg-orange-500/30 rounded-xl blur-lg group-hover:blur-xl transition-all"></div>
                  <div className="relative w-14 h-14 mx-auto bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/40">
                    <Star className="text-white" size={28} />
                  </div>
                </div>
                <h4 className="text-white font-bold text-sm mb-1">Serial Numbered</h4>
                <p className="text-gray-500 text-xs flex-1">Unique ID registry</p>
              </div>
            </div>
          </div>
          
          {/* Bottom Banner */}
          <div className="mt-6 flex justify-center">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 via-transparent to-orange-500/20 rounded-full blur-lg group-hover:blur-xl transition-all"></div>
              <div className="relative inline-flex items-center gap-3 bg-[#12121e]/60 backdrop-blur-sm border border-orange-500/40 rounded-full px-6 py-3">
                <div className="w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center">
                  <Shield className="text-amber-400" size={16} />
                </div>
                <p className="text-gray-200 text-sm">
                  <span className="text-amber-300 font-bold">Military-grade protection</span>
                  <span className="text-gray-500 mx-2">•</span>
                  <span className="text-gray-400">Every card verified</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    {/* CSS for gradient animation */}
    <style jsx>{`
      @keyframes gradient-x {
        0%, 100% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
      }
      .animate-gradient-x {
        background-size: 200% 200%;
        animation: gradient-x 3s ease infinite;
      }
    `}</style>
  </section>
);

const HomePage = ({ authContext }) => {
  const { user, token } = authContext || {};
  const isAuthenticated = !!(user && token);
  const navigate = useNavigate();
  const perfMode = usePerformanceMode();
  const isLowPerf = perfMode === "low";
  const [drops, setDrops] = useState(() => {
    try { const c = JSON.parse(localStorage.getItem('blk-hp-cache')); return c?.drops?.all || []; } catch { return []; }
  });
  const [featuredDrop, setFeaturedDrop] = useState(() => {
    try { const c = JSON.parse(localStorage.getItem('blk-hp-cache')); return c?.drops?.featured?.[0] || null; } catch { return null; }
  });
  const [soldOutDrops, setSoldOutDrops] = useState([]);
  // Category filter for the Upcoming Drops grid (mirrors /drops chips).
  // Category Memory — opens on the user's last-picked bubble (shared key
  // with /drops so both pages remember the same sport).
  const [homeSportFilter, setHomeSportFilter] = useState(() => {
    try {
      const saved = localStorage.getItem("blk-category-filter");
      return saved && SPORT_OPTIONS.some((o) => o.key === saved && o.key !== "all") ? saved : null;
    } catch { return null; }
  });
  useEffect(() => {
    try {
      if (homeSportFilter) localStorage.setItem("blk-category-filter", homeSportFilter);
      else localStorage.removeItem("blk-category-filter");
    } catch { /* private mode */ }
  }, [homeSportFilter]);
  const filteredHomeDrops = homeSportFilter
    ? drops.filter((d) => classifyDrop(d) === homeSportFilter)
    : drops;
  const [stakingRewardDrops, setStakingRewardDrops] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [marketplaceListings, setMarketplaceListings] = useState([]);
  const [trendingMoments, setTrendingMoments] = useState([]);
  const [topPurchases, setTopPurchases] = useState([]);
  const [recentPurchases, setRecentPurchases] = useState([]);
  const [swapSpotStats, setSwapSpotStats] = useState({ sales_volume: 0, purchases: 0, buyers: 0, sellers: 0, big_purchases: 0 });
  const [swapSpotPeriod, setSwapSpotPeriod] = useState('90D');
  // Default stats so UI renders immediately with placeholder values
  const [stats, setStats] = useState({ available: 197, activeDrops: 7, startingPrice: 1, locked: 3 });
  // Default metal prices so UI renders immediately
  const [metalPrices, setMetalPrices] = useState({ gold_blkback: 8.88, silver_blkback: 5.92 });
  const [showGradedNotePopup, setShowGradedNotePopup] = useState(false);
  const [gradedNoteIndex, setGradedNoteIndex] = useState(0);
  const [pageReady, setPageReady] = useState(true); // Start as ready immediately
  const [loadError, setLoadError] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false); // Track when API data is ready
  
  // Bottom video audio control
  const [bottomVideoMuted, setBottomVideoMuted] = useState(true);
  const bottomVideoRef = useRef(null);

  // AR viewer state
  const [showAR, setShowAR] = useState(false);
  const arDemoItem = {
    id: "ar-demo",
    name: "BLK ROBOT",
    rarity: "LEGENDARY",
    image_url: "",
    series: "Series 1",
    edition_number: "001",
  };

  // Hero video audio control
  const [heroVideoMuted, setHeroVideoMuted] = useState(true);
  const heroVideoRef = useRef(null);

  // Dragon Within hero images (fetched from admin settings)
  const [heroImages, setHeroImages] = useState([]);
  
  // Featured pack image for hero (admin-controlled)
  const [heroPack, setHeroPack] = useState(null);
  
  // Hero cards (admin-controlled) for 3D composition
  const [heroCards, setHeroCards] = useState([]);
  
  // Hero 3D model (admin-selected)
  const [heroModelUrl, setHeroModelUrl] = useState(null);
  const [heroModelUsdz, setHeroModelUsdz] = useState(null);
  const [heroPoster, setHeroPoster] = useState(null);
  const [heroModelProgress, setHeroModelProgress] = useState(0);
  const [heroModelLoaded, setHeroModelLoaded] = useState(false);
  const heroViewerRef = useRef(null);

  // Imperatively wire load/progress events on <model-viewer> (React's onLoad
  // props don't fire on web components). Also hide overlay after 8s safety
  // timeout in case neither fires.
  useEffect(() => {
    const el = heroViewerRef.current;
    if (!el || !heroModelUrl) return;
    setHeroModelLoaded(false);
    setHeroModelProgress(0);
    const onLoad = () => { setHeroModelLoaded(true); setHeroModelProgress(1); };
    const onProgress = (e) => {
      const p = e?.detail?.totalProgress;
      if (typeof p === "number") setHeroModelProgress(p);
      if (p >= 1) setHeroModelLoaded(true);
    };
    el.addEventListener("load", onLoad);
    el.addEventListener("progress", onProgress);
    const safety = setTimeout(() => setHeroModelLoaded(true), 8000);
    return () => {
      el.removeEventListener("load", onLoad);
      el.removeEventListener("progress", onProgress);
      clearTimeout(safety);
    };
  }, [heroModelUrl]);
  
  const [activeReview, setActiveReview] = useState(0);

  // Hero callouts — written in real hobby / breaker lingo
  // (grails, hunting autos, hobby smoke, live breaks, refractors,
  // rookies, mojo, ripped, chase, /99, color, parallels, downtown).
  // Pool of 36 distinct shouts. Six are shown per hour, cycling through
  // the pool deterministically based on the current UTC hour so the
  // feed feels genuinely fresh through the day without any backend
  // dependency. No templates — every line is its own callout.
  //
  // If/when we wire this to real user activity, the API can replace
  // `heroCalloutPool` with a server-fed list and the cycling logic
  // below stays identical.
  const heroCalloutPool = [
    { user: "@HobbySmoke",      text: "Hobby smoke straight to my pack — ripped a clean auto first try",      avatar: "HS" },
    { user: "@GrailHunter",     text: "Been hunting autos for years. BLK just gave me my white whale",       avatar: "GH" },
    { user: "@RefractorReaper", text: "That gold refractor /10? CHASE. Pulled it on a live break. Insane",   avatar: "RR" },
    { user: "@BreakerJay",      text: "Live break last night was electric. Rooms stacked, mojo loaded",      avatar: "BJ" },
    { user: "@PrizmBoy",        text: "Prizm hits keep hitting. Sealed → ripped → in my locker. Easy",       avatar: "PB" },
    { user: "@AutoChaser",      text: "Pulled a rookie auto numbered to 99. Heart rate didn't recover",      avatar: "AC" },
    { user: "@DowntownDeals",   text: "Downtown serial right out the pack. BLK keeps cooking",                avatar: "DD" },
    { user: "@ColorOutKing",    text: "Color out my whole vault on BLK. Every parallel hits different",       avatar: "CK" },
    { user: "@RipCity_Sosa",    text: "Pack rip animation alone made me throw my phone. Pulled fire",         avatar: "RC" },
    { user: "@MojoMagnet",      text: "Mojo is real on this app. Three packs, three hits. Locked in",        avatar: "MM" },
    { user: "@SealedRipped",    text: "Sealed → ripped → posted in 30 seconds. Smoothest break I've done",   avatar: "SR" },
    { user: "@AutoHunter_Tre",  text: "Hunting rookie autos and BLK keeps delivering. No cap",                avatar: "AH" },
    { user: "@TheRookieKing",   text: "Rookie patch auto last week, ssp refractor today. We rolling",         avatar: "TK" },
    { user: "@HitDealer404",    text: "Hit dealer hours. Five rips, four bangers. BLK on top",                avatar: "HD" },
    { user: "@SilverStacker",   text: "The metal cards feel like a real grail in hand. Quality is nuts",      avatar: "SS" },
    { user: "@CardCollector",   text: "BLK PACKS rebuilt how I rip. The energy on live is unmatched",         avatar: "CC" },
    { user: "@SuperShortPrint", text: "SSP refractor pulled cold blooded. Numbered /5, mine forever",         avatar: "SP" },
    { user: "@PackPriest",      text: "Blessed every box I rip on here. Pack priest hours",                  avatar: "PP" },
    { user: "@LiveBreakLifer",  text: "Live breaks every night now. Better than streaming. Real action",     avatar: "LL" },
    { user: "@ChasingTheHit",   text: "Chasing the hit since I was 12. BLK finally gave it to me",            avatar: "CH" },
    { user: "@VaultedMojo",     text: "Vault is loaded up. Color, autos, refractors. BLK changed my hobby",   avatar: "VM" },
    { user: "@LowPopLegend",    text: "Low pop hits hit different. Pulled a /25 and immediately listed it",   avatar: "LP" },
    { user: "@BreakerBabe",     text: "Joined a live break random and walked away with three commons + one rare auto. Worth it", avatar: "BB" },
    { user: "@PackOdds_Pete",   text: "Odds feel honest. Real hits actually hit. BLK does it right",          avatar: "PO" },
    { user: "@CrackTheCase",    text: "Cracked a case worth of packs last night. Three autos. I'm done sleeping", avatar: "CT" },
    { user: "@HitOrMiss_Ny",    text: "Hit or miss culture but BLK keeps swinging hits. Daily ripper now",     avatar: "HM" },
    { user: "@DigitalDealer",   text: "Locker hits harder than my real binder. That's how you know",         avatar: "DD2" },
    { user: "@RippedAndReady",  text: "Pack rip → moment video → straight to socials. Cleanest UX in the hobby", avatar: "RA" },
    { user: "@AutoMode",        text: "Auto mode unlocked. Rookie auto + numbered refractor + free first rip", avatar: "AM" },
    { user: "@MojoLoaded",      text: "Mojo loaded up on my account. Three rips deep, three bangers",         avatar: "ML" },
    { user: "@TheGradeChase",   text: "Grading the pulls IRL after BLK ships. Real card, real moment",         avatar: "GC" },
    { user: "@HobbyKingdom",    text: "Hobby kingdom is back and BLK is wearing the crown",                    avatar: "HK" },
    { user: "@ChaseCardCarl",   text: "Chase card pulled live in front of 300 people. Heart still pounding",   avatar: "CC2" },
    { user: "@PaperHandsNoMo",  text: "No more paper hands. Vault locked. BLK got me holding for the long",    avatar: "PH" },
    { user: "@RippinRickyy",    text: "Rippin Ricky here. Live break tonight. Bring your mojo",                avatar: "RR2" },
    { user: "@PullOfTheDay",    text: "Pull of the day went to me. Refractor auto /50. BLK is the wave",       avatar: "PD" },
  ];

  // Pick 6 callouts deterministically based on the current UTC hour so
  // the homepage feels fresh through the day without server roundtrips.
  // Same hour → same 6 → consistent for everyone landing within that
  // window. Next hour rolls forward by 6 (with wrap-around).
  const heroReviews = useMemo(() => {
    const hour = Math.floor(Date.now() / (60 * 60 * 1000));
    const start = (hour * 6) % heroCalloutPool.length;
    const slice = [];
    for (let i = 0; i < 6; i++) {
      slice.push(heroCalloutPool[(start + i) % heroCalloutPool.length]);
    }
    return slice;
    // heroCalloutPool is a static const inside the component — no need
    // to thread it through deps; recomputed once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Cycle through reviews
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveReview(prev => (prev + 1) % heroReviews.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [heroReviews.length]);

  // Force play hero video immediately and when visible
  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video) return;
    // Play immediately
    video.play().catch(() => {});
    // Also play on visibility change (tab switch, scroll back)
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  // Force play bottom video when visible
  
  // Auto-rotate graded note images for multi-card packs
  useEffect(() => {
    const drop = featuredDrop || drops[0];
    const noteImages = drop?.graded_note_images?.filter(img => img && img.trim() !== '') || [];
    if (noteImages.length <= 1) return;
    const interval = setInterval(() => {
      setGradedNoteIndex(prev => (prev + 1) % noteImages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [featuredDrop, drops]);
  // Force play bottom video immediately and when visible
  useEffect(() => {
    const video = bottomVideoRef.current;
    if (!video) return;
    video.play().catch(() => {});
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        }
      },
      { threshold: 0.1 }
    );
    
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Track QR code scan if user arrived via QR
    const params = new URLSearchParams(window.location.search);
    if (params.get('src') === 'qr') {
      axios.post(`${API}/qr/track-scan?source=qr_home`).catch(() => {});
    }

    // BATCHED: Fetch all homepage data in a single request
    axios.get(`${API}/homepage-data?period=${swapSpotPeriod}`).then(res => {
      const data = res.data;
      
      // Cache response for instant rendering on next visit
      try { localStorage.setItem('blk-hp-cache', JSON.stringify(data)); } catch {}
      
      // Preload critical hero images immediately
      const fd = data.drops?.featured?.[0] || (data.drops?.all || [])[0];
      if (fd) {
        const preloadUrls = [
          fd.sealed_image_url || fd.image_url,
          fd.graded_note_image_url,
          ...(fd.graded_note_images || [])
        ].filter(Boolean);
        preloadUrls.forEach(url => {
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = getMediaUrl(url);
          document.head.appendChild(link);
        });
      }
      
      // Hero images from site settings
      if (data.hero_images && data.hero_images.length > 0) {
        setHeroImages(data.hero_images);
      }
      
      // Featured pack for hero section
      if (data.hero_pack && data.hero_pack.image_url) {
        setHeroPack(data.hero_pack);
      }
      
      // Fetch hero cards separately
      axios.get(`${API}/hero-cards`).then(heroCardsRes => {
        if (heroCardsRes.data?.cards?.length > 0) {
          setHeroCards(heroCardsRes.data.cards);
        }
      }).catch(() => {
        console.log('Hero cards not configured yet');
      });
      
      // Fetch admin-selected hero 3D model
      axios.get(`${API}/ar/hero-model`).then(res => {
        // Convert local model paths to R2 CDN for faster global delivery
        const toR2 = (url) => {
          if (url && url.startsWith('/api/ar/model/')) {
            const filename = url.replace('/api/ar/model/', '');
            return `https://media.blkpacks.fun/ar-models/${encodeURIComponent(filename)}`;
          }
          return url;
        };
        if (res.data?.model_url) {
          const resolvedModel = toR2(res.data.model_url);
          setHeroModelUrl(resolvedModel);
          // Kick off the GLB download immediately so model-viewer finds it cached
          // by the time it mounts — eliminates the "blank frame" window.
          try {
            const existing = document.querySelector(`link[data-hero-prefetch="${resolvedModel}"]`);
            if (!existing) {
              const link = document.createElement('link');
              link.rel = 'preload';
              link.as = 'fetch';
              link.href = resolvedModel;
              link.crossOrigin = 'anonymous';
              link.setAttribute('data-hero-prefetch', resolvedModel);
              document.head.appendChild(link);
            }
          } catch { /* ignore */ }
        }
        if (res.data?.model_url_usdz) {
          setHeroModelUsdz(toR2(res.data.model_url_usdz));
        }
        if (res.data?.poster_url) {
          setHeroPoster(res.data.poster_url);
        }
      }).catch(() => {});
      
      // Drops
      if (data.drops?.featured?.length > 0) {
        setFeaturedDrop(data.drops.featured[0]);
      }
      const allDrops = data.drops?.all || [];
      setDrops(allDrops);
      const totalAvailable = allDrops.reduce((sum, d) => sum + (d.packs_remaining || 0), 0);
      const totalLocked = allDrops.reduce((sum, d) => sum + (d.locked_count || 0), 0);
      const minPrice = allDrops.length > 0 ? Math.min(...allDrops.map(d => d.price)) : 1;
      setStats({
        available: totalAvailable || 197,
        activeDrops: allDrops.length || 7,
        startingPrice: minPrice || 1,
        locked: totalLocked || 3
      });
      setDataLoaded(true);
      
      setSoldOutDrops(data.drops?.sold_out || []);
      
      // Staking rewards
      const featuredRewards = (data.staking_rewards || []).filter(d => d.show_on_homepage);
      setStakingRewardDrops(featuredRewards);
      
      // Challenges
      setChallenges(data.challenges || []);
      
      // Marketplace
      const listings = data.marketplace?.listings || [];
      setMarketplaceListings(listings);
      setTrendingMoments(data.marketplace?.trending?.length > 0 ? data.marketplace.trending : listings.slice(0, 6));
      setTopPurchases(data.marketplace?.top_purchases?.length > 0 ? data.marketplace.top_purchases : listings.slice(0, 6));
      setRecentPurchases(data.marketplace?.recent_purchases?.length > 0 ? data.marketplace.recent_purchases : listings.slice(0, 6));
      
      // Metal prices
      if (data.metal_prices) {
        setMetalPrices(data.metal_prices);
      }
    }).catch((err) => {
      console.error('Failed to fetch homepage data:', err);
      setDataLoaded(true);
    });
    
    // Swap stats auto-refresh (independent, less frequent)
    const fetchSwapStats = () => {
      axios.get(`${API}/marketplace/stats?period=${swapSpotPeriod}`).then(res => {
        setSwapSpotStats(res.data || {});
      }).catch(() => {});
    };
    fetchSwapStats();
    const swapStatsInterval = setInterval(fetchSwapStats, 30 * 60 * 1000);
    
    // Metal prices auto-refresh (every 30s for live ticker)
    const fetchMetalPrices = () => {
      axios.get(`${API}/metal-prices`).then(res => {
        setMetalPrices(res.data);
      }).catch(() => {});
    };
    const priceInterval = setInterval(fetchMetalPrices, 30000);
    
    return () => {
      clearInterval(priceInterval);
      clearInterval(swapStatsInterval);
    };
  }, []);

  const howItWorks = [
    {
      icon: CreditCard,
      title: "Get Pack Points",
      description: "Purchase points to unlock challenges and exclusive drops."
    },
    {
      icon: Package,
      title: "Join Drops",
      description: "Buy packs from limited releases. Get physical + digital collectibles shipped to you."
    },
    {
      icon: Trophy,
      title: "Enter Challenges",
      description: "Compete for exclusive rewards. Winners get certified collectible cards."
    },
    {
      icon: Shield,
      title: "Certified Authentic",
      description: "Every item includes a digital certificate with verified serial number."
    },
    {
      icon: Lock,
      title: "Lock In Rewards",
      description: "Lock moments for 30-90 days. Earn FREE physical collectibles + boosts."
    },
    {
      icon: Store,
      title: "Swap Spot",
      description: "Trade collectibles with other collectors or list yours for exchange."
    }
  ];

  // Toggle hero video audio
  const toggleHeroVideoAudio = () => {
    if (heroVideoRef.current) {
      heroVideoRef.current.muted = !heroVideoRef.current.muted;
      setHeroVideoMuted(heroVideoRef.current.muted);
    }
  };

  // Toggle bottom video audio
  const toggleBottomVideoAudio = () => {
    if (bottomVideoRef.current) {
      bottomVideoRef.current.muted = !bottomVideoRef.current.muted;
      setBottomVideoMuted(bottomVideoRef.current.muted);
    }
  };
  
  // Handle Swap Spot period change
  const handleSwapSpotPeriodChange = (period) => {
    setSwapSpotPeriod(period);
    axios.get(`${API}/marketplace/stats?period=${period}`).then(res => {
      setSwapSpotStats(res.data || {});
    }).catch(() => {});
  };

  // Home hero backdrop refs + parallax — mirrors the Drops/Sell hero
  // treatment but anchored to the LEFT side so the cosmic basketball
  // poster sits opposite to the Drops football player. ~25% slow drift
  // on scroll, disabled for reduced-motion users and narrow mobile.
  const homeHeroSectionRef = useRef(null);
  const homeHeroBackdropRef = useRef(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;
    if (window.innerWidth < 640) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const section = homeHeroSectionRef.current;
      const bg = homeHeroBackdropRef.current;
      if (!section || !bg) return;
      const rect = section.getBoundingClientRect();
      const offset = -rect.top * 0.25;
      bg.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
    };
    const onScroll = () => { if (!raf) raf = window.requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div 
      className="bg-[#12121e] text-white overflow-x-hidden" 
      style={{ 
        minHeight: '100vh',
        minHeight: '-webkit-fill-available',
        position: 'relative'
      }}
      data-testid="homepage-container"
      data-perf={perfMode}
    >

      {/* ────────── HOME HERO BACKDROP ──────────
          Photographic backdrop anchored to the LEFT side of the page —
          mirror of the Drops page (Bears player anchored right). Uses
          the same `bg-no-repeat / contain / parallax` recipe so both
          pages share a single visual language. `pointer-events-none`
          keeps every link/button above it clickable. `aria-hidden` so
          screen readers ignore the decorative photo. */}
      <section
        ref={homeHeroSectionRef}
        className="absolute inset-x-0 top-0 h-[680px] sm:h-[760px] overflow-hidden pointer-events-none"
        data-testid="home-hero-backdrop"
        aria-hidden="true"
      >
        <div
          ref={homeHeroBackdropRef}
          className="absolute inset-0 bg-no-repeat opacity-65 will-change-transform"
          style={{
            backgroundImage: "url(/static-hero/home_hero_backdrop.png)",
            backgroundSize: "contain",
            backgroundPosition: "right center",
          }}
        />
        {/* Layered gradients — black wash on the LEFT for typography
            contrast (mirror of Drops), top fade so the navbar stays
            clean, deep bottom fade so the upcoming-drops grid below
            sits cleanly on solid black. */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/65 to-black/35" />
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-black via-black/90 to-transparent" />
      </section>
      
      
      {/* Top-of-page anchor (RUN IT ticker + tournament + live matches
          moved below the AR/CTA section — see `runit-homepage-strip`) */}

      {/* AR Experience hero REMOVED 2026-05-15 per user directive.
          Page now leads with Live/Upcoming Drops (moved up below). */}
      {false && (
      <SectionErrorBoundary name="ar-experience">
      <StickyScrollSection
        bgColor="bg-[#050507]"
        className="relative pt-12 md:pt-16 pb-8 md:pb-16 px-4 relative overflow-hidden"
        zIndex={10}
        isFirst={true}
      >
        {/* Background pulse */}
        {!isLowPerf && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-orange-600/4 rounded-full blur-[200px]" style={{ animation: 'arGlowPulse 4s ease-in-out infinite' }} />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-orange-600/4 rounded-full blur-[200px]" style={{ animation: 'arGlowPulse 4s ease-in-out infinite 2s' }} />
        </div>
        )}

        <div className="max-w-6xl mx-auto relative z-10" data-testid="ar-promo-section">
          {/* Header copy block + NOW LIVE pill removed per product call —
              the section now leads straight with the BLK pack card. */}

          {/* AR Card 3D Model + QR Code side by side */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12 mb-12">
            {/* BLK Card Frame with interactive 3D model */}
            <div className="flex-shrink-0 relative">
              <div className="absolute -inset-8 bg-orange-500/8 rounded-full blur-[60px]" />
              <div className="relative w-[280px] sm:w-[340px]" data-testid="ar-3d-card-frame">
                {/* Card border frame */}
                <div className="rounded-xl overflow-hidden card-spectrum card-spectrum-epic shadow-[0_20px_60px_-10px_rgba(249,115,22,0.35)] bg-[#0a0a14]">
                  {/* Card header */}
                  <div className="flex items-center justify-between px-2.5 py-1.5 bg-[#0c0c18]">
                    <span className="text-white text-[9px] font-black tracking-wider">BLK <span className="text-gray-500 text-[8px] font-normal">PACKS</span></span>
                    <div className="flex items-center gap-1.5">
                      <div className="bg-green-500/20 border border-green-500/40 rounded-full px-1.5 py-0.5 flex items-center gap-1">
                        <div className="w-1 h-1 rounded-full bg-green-400 animate-pulse" />
                        <span className="text-green-400 text-[7px] font-bold">AR LIVE</span>
                      </div>
                      <span className="bg-orange-500/80 text-white text-[7px] font-bold px-1.5 py-0.5 rounded">AR</span>
                    </div>
                  </div>
                    {/* 3D Model area — ANIMATED */}
                  <div className="aspect-[3/4] bg-black relative overflow-hidden">
                    {/* BLK PACKS branded sleeve — the ONLY thing shown behind
                        the 3D model while it loads. No cycling through pack
                        images, drop images, or posters. Just the logo.
                        Fades out once the model fully loads. */}
                    <img
                      src={HERO_FALLBACK_DATAURI}
                      alt=""
                      fetchPriority="high"
                      loading="eager"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-500 z-[1]"
                      style={{ opacity: heroModelLoaded ? 0 : 1 }}
                    />
                    {heroModelUrl ? (
                    <>
                    <model-viewer
                      ref={heroViewerRef}
                      src={(() => {
                        const url = heroModelUrl.startsWith('/api') ? `${API.replace('/api', '')}${heroModelUrl}` : heroModelUrl;
                        return encodeURI(url);
                      })()}
                      {...(heroModelUsdz ? { "ios-src": (() => {
                        const url = heroModelUsdz.startsWith('/api') ? `${API.replace('/api', '')}${heroModelUsdz}` : heroModelUsdz;
                        return encodeURI(url);
                      })() } : {})}
                      poster={heroPoster || undefined}
                      reveal="auto"
                      autoplay
                      auto-rotate
                      camera-controls
                      touch-action="pan-y"
                      rotation-per-second="30deg"
                      shadow-intensity="0.8"
                      environment-image="neutral"
                      exposure="1.2"
                      camera-orbit="0deg 75deg 140%"
                      min-camera-orbit="auto auto 100%"
                      max-camera-orbit="auto auto 300%"
                      field-of-view="30deg"
                      min-field-of-view="20deg"
                      max-field-of-view="50deg"
                      animation-name="Dance"
                      ar
                      ar-scale="auto"
                      ar-placement="floor"
                      loading="eager"
                      fetchPriority="high"
                      crossorigin="anonymous"
                      style={{
                        width: "100%",
                        height: "100%",
                        position: "absolute",
                        top: "0",
                        zIndex: 2,
                        opacity: heroModelLoaded ? 1 : 0,
                        transition: "opacity 450ms ease",
                        background: "radial-gradient(ellipse at center, rgba(249,115,22,0.15) 0%, #000 70%)",
                      }}
                      data-testid="ar-homepage-3d-model"
                    />
                    {/* Loading progress overlay — hidden once model is ready */}
                    {!heroModelLoaded && (
                      <div className="absolute inset-x-0 bottom-0 px-3 py-2 bg-gradient-to-t from-black/80 to-transparent pointer-events-none flex flex-col gap-1">
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                          <span className="text-amber-300 text-[9px] font-bold uppercase tracking-wider">Loading 3D · {Math.round(heroModelProgress * 100)}%</span>
                        </div>
                        <div className="h-0.5 bg-white/10 rounded overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-amber-400 to-amber-400 transition-all duration-300"
                            style={{ width: `${Math.round(heroModelProgress * 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                    </>
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-8 h-8 border-2 border-orange-500/40 border-t-orange-500 rounded-full animate-spin" />
                      </div>
                    )}
                    {/* EMERGE ledger stamp — bottom-LEFT, clear of the
                        model-viewer AR cube (which is pinned bottom-right). */}
                    <div
                      className="absolute bottom-1.5 left-1.5 z-10 drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)] pointer-events-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <EmergeBadge
                        size="sm"
                        showDot={false}
                        verificationId="AR-DROPS-S1-001"
                        cardName="AR Drops · Series 1"
                      />
                    </div>
                  </div>
                  {/* Card footer */}
                  <div className="px-2.5 py-2 bg-[#0c0c18] flex items-center justify-between">
                    <div>
                      <p className="text-white text-[10px] font-black uppercase tracking-wide">AR Drops</p>
                      <p className="text-gray-500 text-[8px]">Series 1</p>
                    </div>
                    <p className="text-gray-500 text-[8px] font-mono">#001/500</p>
                  </div>
                </div>
              </div>
            </div>

            {/* AR QR Code sample */}
            <div className="flex-shrink-0 text-center">
              <div className="relative w-[140px] sm:w-[160px] mx-auto">
                <div className="absolute -inset-4 bg-white/5 rounded-2xl blur-xl" />
                <img
                  src={`${API}/ar/qr/hero.png?domain=https://blkpacks.fun`}
                  alt="AR QR Code"
                  className="relative w-full h-auto rounded-xl shadow-[0_10px_40px_rgba(249,115,22,0.2)]"
                  loading="lazy"
                  data-testid="ar-qr-sample-img"
                />
              </div>
              <p className="text-amber-400 text-[10px] font-bold mt-3 tracking-wider uppercase">Scan to AR</p>
              <p className="text-gray-500 text-[9px] mt-0.5 max-w-[160px]">Every printed card includes a unique AR QR code</p>
            </div>
          </div>

          {/* "How It Works" 01-05 strip removed per user request — was noisy
              and the user wanted a cleaner flow straight into the CTA row. */}

          {/* Bottom CTA */}
          {/* CTA Buttons — unified brand-gradient + shimmer flash. */}
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-center gap-3 px-4 md:px-0">
            <button
              onClick={() => isAuthenticated ? navigate("/locker") : navigateToLogin(navigate, "/locker")}
              className="w-full md:w-auto md:min-w-[180px] brand-gradient-btn font-black text-sm px-8 py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-orange-500/30"
              data-testid="ar-trade-cta-btn"
            >
              <RefreshCw size={15} />
              TRADE
            </button>

            <button 
              onClick={() => navigate("/drops")}
              className="w-full md:w-auto md:min-w-[180px] brand-gradient-btn font-black text-sm px-8 py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-orange-500/30"
              data-testid="enter-drops-btn"
            >
              <Play size={15} fill="black" />
              ENTER DROPS
            </button>

            <button 
              onClick={() => navigate("/wallet")}
              className="w-full md:w-auto md:min-w-[180px] brand-gradient-btn font-black text-sm px-8 py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-orange-500/30"
              data-testid="buy-pack-points-btn"
            >
              <Coins size={15} />
              BUY POINTS
            </button>

            {/* CLAIM CARD — links to the serial-claim flow. Visible signed-in + signed-out. */}
            <button
              onClick={() => navigate("/claim")}
              className="w-full md:w-auto md:min-w-[180px] brand-gradient-btn font-black text-sm px-8 py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-orange-500/30"
              data-testid="hero-claim-card-btn"
            >
              <Package size={15} />
              CLAIM CARD
            </button>
            
            {(user && !user.welcome_pack_claimed) || !user ? (
              <button 
                onClick={() => navigate("/first-rip")}
                className="w-full md:w-auto md:min-w-[180px] brand-gradient-btn-green font-black text-sm px-8 py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/40"
                data-testid="claim-free-pack-btn"
              >
                <Zap size={15} />
                FREE PACK
              </button>
            ) : null}
          </div>
        </div>

        <style>{`
          @keyframes arGlowPulse {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 0.8; }
          }
          @keyframes arBadgeBob {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-4px); }
          }
        `}</style>
      </StickyScrollSection>
      </SectionErrorBoundary>
      )}

      {/* SOLD-OUT TICKER — public, top-of-funnel scarcity proof. Renders
          only when a First Rip series has rotated in the last 7 days; otherwise
          returns null so the homepage layout is untouched. Works for both
          signed-in and signed-out visitors. Wrapped in the error boundary so
          a ticker bug can never blank the homepage. `relative` so it
          paints ABOVE the absolute hero backdrop. */}
      <div className="relative">
        <SectionErrorBoundary name="first-rip-rotation-ticker">
          <FirstRipRotationTicker />
        </SectionErrorBoundary>
      </div>

      {/* LIVE BREAK HERO moved BELOW Upcoming Drops 2026-06-02 per user —
          see the `live-break-hero` boundary further down. The top of the
          homepage is now reserved for the photographic Bears hero backdrop
          + Upcoming Drops grid. */}

      {/* UPCOMING DROPS Section — MOVED to top 2026-05-15 per user directive.
          Now the first visible section on the homepage (signed-in & out).
          bgColor is intentionally transparent so the LEFT-anchored hero
          backdrop (`home_hero_backdrop.png`) reads through behind the
          centered drops grid — same treatment as `/drops`. */}
      <SectionErrorBoundary name="upcoming-drops">
      <StickyScrollSection 
        bgColor="bg-transparent"
        className="py-12 md:py-16 px-4 relative overflow-hidden"
        zIndex={15}
      >
        {/* Subtle glow - hidden on low-perf devices */}
        {!isLowPerf && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden hidden md:block">
          <div className="absolute top-20 right-1/4 w-[400px] h-[400px] bg-orange-500/5 rounded-full blur-[180px]"></div>
          <div className="absolute bottom-20 left-1/3 w-[300px] h-[300px] bg-orange-500/3 rounded-full blur-[150px]"></div>
        </div>
        )}
        
        <div className="max-w-6xl mx-auto relative z-10 pt-8">
          <AnimatedSection animation="fade-up">
            {/* LIVE NOW chip — sits ABOVE the Upcoming Drops heading. Shows
                LIVE pulse + active drops count + packs remaining + the
                soonest-ending drop's countdown. Pulled from the existing
                stats hook + `drops` array — no new endpoint. Added
                2026-05-15 per user request. */}
            {stats.activeDrops > 0 && (() => {
              const now = Date.now();
              const earliestEndMs = drops
                .map((d) => d?.ends_at ? Date.parse(d.ends_at) : 0)
                .filter((t) => t && t > now)
                .sort((a, b) => a - b)[0];
              const fmt = (ms) => {
                if (!ms) return null;
                const s = Math.max(0, Math.floor((ms - now) / 1000));
                const d = Math.floor(s / 86400);
                const h = Math.floor((s % 86400) / 3600);
                const m = Math.floor((s % 3600) / 60);
                if (d > 0) return `${d}d ${h}h`;
                if (h > 0) return `${h}h ${m}m`;
                return `${m}m`;
              };
              const ends = fmt(earliestEndMs);
              return (
                <div className="flex justify-center mb-4" data-testid="upcoming-drops-live-chip">
                  <Link
                    to="/live"
                    className="inline-flex items-center gap-2 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 hover:border-emerald-500/50 rounded-full px-3.5 py-1.5 transition-colors"
                    data-testid="upcoming-drops-live-chip-link"
                  >
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                    </span>
                    <span className="text-emerald-300 font-black text-[10px] tracking-widest">LIVE NOW</span>
                    <span className="text-white/20 text-[10px]">·</span>
                    <span className="text-white font-bold text-[10px]">{stats.activeDrops} {stats.activeDrops === 1 ? 'drop' : 'drops'}</span>
                    {stats.available > 0 && (
                      <>
                        <span className="text-white/20 text-[10px]">·</span>
                        <span className="text-amber-300 font-bold text-[10px]">{stats.available} packs</span>
                      </>
                    )}
                    {ends && (
                      <>
                        <span className="text-white/20 text-[10px]">·</span>
                        <span className="text-white/70 text-[10px]">ends in {ends}</span>
                      </>
                    )}
                  </Link>
                </div>
              );
            })()}
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-orange-500 rounded-full shadow-lg shadow-orange-500/50 animate-pulse"></div>
                  <span className="text-amber-300 text-xs uppercase tracking-[0.2em] font-medium">Live Drops</span>
                </div>
              <h2 className="text-3xl md:text-4xl font-black">
                <span className="text-white">Upcoming </span>
                <span className="brand-gradient-text">Drops</span>
              </h2>
            </div>
            <Link to="/drops" className="group flex items-center gap-2 text-amber-300 hover:text-orange-200 text-sm font-medium transition-colors">
              View All <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          {/* Reviews ticker — sits BELOW the Upcoming Drops heading. Cycles
              through fake community reactions (heroReviews state) every
              2.5s with vertical fade-slide. Brought back here 2026-05-15
              after the AR/Own-the-Moments hero was removed. */}
          <div className="relative h-12 overflow-hidden mb-8 mt-1" data-testid="home-reviews-ticker">
            {heroReviews.map((review, idx) => (
              <div
                key={idx}
                className={`absolute inset-0 flex items-center justify-center gap-3 transition-all duration-500 ${
                  idx === activeReview
                    ? 'opacity-100 translate-y-0'
                    : idx === (activeReview - 1 + heroReviews.length) % heroReviews.length
                      ? 'opacity-0 -translate-y-6'
                      : 'opacity-0 translate-y-6'
                }`}
              >
                <div className="flex items-center gap-2.5 bg-white/[0.04] border border-white/[0.08] backdrop-blur-sm rounded-full px-4 py-2">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-[8px]">{review.avatar}</span>
                  </div>
                  <span className="text-amber-400 font-bold text-xs">{review.user}</span>
                  <span className="text-gray-400 text-xs">{review.text}</span>
                </div>
              </div>
            ))}
          </div>
          </AnimatedSection>

          {/* Category filter chips — same bubbles as /drops, always all visible */}
          <div
            className="flex gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 pb-1 mb-6"
            style={{ WebkitOverflowScrolling: "touch" }}
            data-testid="home-sport-filter-chips"
          >
            {SPORT_OPTIONS.map((opt) => {
              const isActive = (opt.key === "all" && !homeSportFilter) || homeSportFilter === opt.key;
              const count = opt.key === "all"
                ? drops.length
                : drops.filter((d) => classifyDrop(d) === opt.key).length;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setHomeSportFilter(opt.key === "all" ? null : opt.key)}
                  data-testid={`home-sport-chip-${opt.key}`}
                  className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                    isActive
                      ? "brand-gradient-bg text-black shadow-lg shadow-orange-500/30"
                      : "bg-white/[0.06] text-white/70 hover:bg-white/[0.1] hover:text-white border border-white/10"
                  }`}
                >
                  <span>{opt.label}</span>
                  {count > 0 && (
                    <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${
                      isActive ? "bg-black/20 text-black" : "bg-white/10 text-white/60"
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          
          {!dataLoaded ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <CardSkeleton key={i} className="h-[180px]" />
              ))}
            </div>
          ) : filteredHomeDrops.length > 0 ? (
            <UpcomingDropsCarousel drops={filteredHomeDrops.slice(0, 12)} navigate={navigate} />
          ) : (
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-800/50 flex items-center justify-center">
                <Package className="text-gray-600" size={32} />
              </div>
              <p className="text-gray-500 text-lg">No upcoming drops</p>
            </div>
          )}
          
          {/* Explore All Drops CTA Button */}
          <div className="text-center mt-10 pb-6">
            <button 
              onClick={() => navigate("/drops")}
              className="group relative inline-flex items-center gap-2"
              data-testid="explore-all-drops-btn"
            >
              <div className="relative bg-[#0c0c0c] border border-orange-500/20 hover:border-orange-500/40 rounded-full px-8 py-3 text-amber-300 font-bold text-sm transition-all flex items-center gap-2">
                <Play size={16} />
                All Drops
              </div>
            </button>
          </div>
        </div>
      </StickyScrollSection>
      </SectionErrorBoundary>

      {/* LIVE BREAK HERO — moved below Upcoming Drops 2026-06-02 per user
          directive so the hero backdrop image at the very top of the page
          stays clean. Picks the soonest live / scheduled break and feature-
          plays it. Renders null when there are no breaks. Replaces the
          old RUN IT bot strip (FeaturedTournamentBanner + LiveMatchesWidget)
          that previously occupied this slot. */}
      <SectionErrorBoundary name="live-break-hero">
        <LiveBreakHero />
      </SectionErrorBoundary>

      {/* SOLD OUT DROPS Section - STICKY SCROLL */}
      {soldOutDrops.length > 0 && (
        <SectionErrorBoundary name="sold-out-drops">
        <StickyScrollSection 
          bgColor="bg-gradient-to-b from-[#0a0a0c] via-[#080808] to-[#060606]"
          className="py-12 md:py-16 px-4 relative overflow-hidden"
          zIndex={25}
        >
          {/* Subtle background - hidden on low-perf */}
          {!isLowPerf && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden hidden md:block">
            <div className="absolute top-20 left-1/4 w-[300px] h-[300px] bg-red-500/3 rounded-full blur-[150px]"></div>
          </div>
          )}
          
          <div className="max-w-6xl mx-auto relative z-10 pt-8">
            {/* Section Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={14} className="text-red-400" />
                  <span className="text-red-400 text-xs uppercase tracking-[0.2em] font-medium">Past Collections</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-black">
                  <span className="text-white">Sold Out </span>
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-red-600">Drops</span>
                </h2>
              </div>
              <div className="bg-[#0c0c0c] border border-red-500/20 rounded-full px-4 py-2 flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-red-400 text-xs font-bold">{soldOutDrops.length} SOLD OUT</span>
              </div>
            </div>
            
            {/* Sold Out Drops Grid — NBA Top Shot style */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {soldOutDrops.slice(0, 4).map((drop) => (
                <div 
                  key={drop.id} 
                  className="bg-[#12121e] rounded-xl overflow-hidden transition-all duration-300 group"
                >
                  {/* BLK Card Frame — grayscale for sold out */}
                  <div className="relative opacity-60 grayscale-[30%]">
                    <BLKCardFrame
                      imageUrl={drop.pack_image_url || drop.card_images?.[0] || drop.image_url}
                      videoUrl={drop.video_url}
                      name={drop.name}
                      rarity={drop.drop_rarity}
                      isHobby={!!drop.is_hobby_drop}
                      series={getHobbySeriesLabel(drop)}
                      isAR={drop.is_ar_drop}
                      aspectRatio="4/5"
                      disableSpectrum
                      dropId={drop.id}
                    verificationId={drop.verification_id}
                  />
                    {/* Diagonal SOLD OUT Banner */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                      <div className="bg-red-600/90 px-4 py-1.5 rounded-lg transform -rotate-6">
                        <span className="text-white font-black text-sm tracking-widest">SOLD OUT</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Info Section */}
                  <div className="px-3 pb-3 pt-2">
                    <h3 className="text-white font-black text-sm uppercase leading-tight truncate">{drop.name}</h3>
                    <div className="text-gray-500 text-[10px] mt-1">
                      {drop.total_packs} minted · {drop.price} pts
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* View All Past Drops */}
            {soldOutDrops.length > 4 && (
              <div className="text-center mt-10 pb-6">
                <button 
                  onClick={() => navigate("/drops")}
                  className="group relative inline-flex items-center gap-2"
                  data-testid="view-past-drops-btn"
                >
                  <div className="relative bg-[#0c0c0c] border border-red-500/20 hover:border-red-500/40 rounded-full px-8 py-3 text-red-400 font-bold text-sm transition-all flex items-center gap-2">
                    <Clock size={16} />
                    Past Drops
                  </div>
                </button>
              </div>
            )}
          </div>
        </StickyScrollSection>
        </SectionErrorBoundary>
      )}

      {/* OWN THE MOMENTS hero REMOVED 2026-05-15 per user directive. */}
      {false && (
      <SectionErrorBoundary name="own-the-moments">
      <StickyScrollSection 
        bgColor="bg-black"
        className="relative py-12 md:py-16 overflow-hidden"
        zIndex={30}
      >
        {/* Ambient glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-orange-600/8 rounded-full blur-[180px]"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 relative z-10">
          {/* 3D Pack & Flying Cards Composition */}
          <div className="relative mx-auto mb-2 h-[300px] sm:h-[360px] md:h-[400px]" style={{ maxWidth: '640px', perspective: '1200px' }} data-testid="hero-3d-composition">
            
            {/* Resolve card data: use admin heroCards if set, otherwise fall back to drops */}
            {(() => {
              const card1 = heroCards[0] || {};
              const card2 = heroCards[1] || {};
              const fallbackCard1 = drops[0] || {};
              const fallbackCard2 = drops[1] || {};
              
              const c1Video = card1.video_url || '';
              const c1Image = card1.image_url || getMediaUrl(fallbackCard1?.card_images?.[0] || fallbackCard1?.image_url || '');
              const c1Name = card1.name || fallbackCard1?.name || 'BLK PACKS CARD';
              const c1Rarity = card1.rarity || 'COMMON';
              const c1Supply = card1.supply || 5000;
              const c1Series = card1.series || 'Series 1';
              
              const c2Video = card2.video_url || '';
              const c2Image = card2.image_url || getMediaUrl(fallbackCard2?.card_images?.[0] || fallbackCard2?.image_url || '');
              const c2Name = card2.name || fallbackCard2?.name || 'BLK MOMENT';
              const c2Rarity = card2.rarity || 'RARE';
              const c2Supply = card2.supply || 1000;
              const c2Series = card2.series || 'Series 1';
              
              const rarityColor = (r) => r === 'LEGENDARY' ? 'text-amber-400' : r === 'EPIC' ? 'text-amber-400' : r === 'RARE' ? 'text-amber-400' : 'text-emerald-400';
              
              return (
                <>
            {/* Card 2 — Back card, upper-left, more tilted */}
            <div 
              className="absolute z-10 w-[140px] sm:w-[170px] md:w-[200px]"
              style={{
                top: '0%', left: '0%',
                animation: 'heroFloat2 6s ease-in-out infinite',
                transformStyle: 'preserve-3d',
                transform: 'rotateY(-8deg) rotateX(6deg) rotateZ(-15deg)',
              }}
            >
              <BLKCardFrame
                imageUrl={c2Image}
                videoUrl={c2Video}
                name={c2Name}
                rarity={c2Rarity}
                serial={`/${c2Supply}`}
                series={c2Series}
                verificationId={card2.verification_id || fallbackCard2?.verification_id}
              />
            </div>
            
            {/* Card 1 — Front card, center, slight tilt */}
            <div 
              className="absolute z-20 w-[160px] sm:w-[190px] md:w-[220px]"
              style={{
                top: '10%', left: '18%',
                animation: 'heroFloat1 5s ease-in-out infinite',
                transformStyle: 'preserve-3d',
                transform: 'rotateY(-5deg) rotateX(4deg) rotateZ(-6deg)',
              }}
            >
              <BLKCardFrame
                imageUrl={c1Image}
                videoUrl={c1Video}
                name={c1Name}
                rarity={c1Rarity}
                serial={`/${c1Supply}`}
                series={c1Series}
                verificationId={card1.verification_id || fallbackCard1?.verification_id}
              />
            </div>
                </>
              );
            })()}
            
            {/* Pack — Right side, angled like reference image */}
            <div 
              className="absolute z-30 w-[130px] sm:w-[160px] md:w-[190px]"
              style={{
                top: '8%', right: '0%',
                animation: 'heroFloat3 7s ease-in-out infinite',
                transformStyle: 'preserve-3d',
                transform: 'rotateY(12deg) rotateX(-6deg) rotateZ(8deg)',
              }}
            >
              {/* Pack glow effect */}
              <div className="absolute -inset-6 bg-orange-500/8 rounded-2xl blur-3xl"></div>
              
              {heroPack?.image_url ? (
                <ResponsiveImage
                  src={getMediaUrl(heroPack.image_url)}
                  alt={heroPack.label || "BLK PACK"}
                  className="relative w-full drop-shadow-2xl"
                  style={{ filter: 'drop-shadow(0 25px 50px rgba(0,0,0,0.6))' }}
                  loading="eager"
                  fetchPriority="high"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
                  data-testid="hero-featured-pack"
                />
              ) : (
                <div className="relative bg-gradient-to-b from-gray-800 to-gray-900 rounded-lg aspect-[2/3] flex items-center justify-center border border-white/10">
                  <div className="text-center">
                    <Package className="text-amber-400 mx-auto mb-2" size={36} />
                    <span className="text-white font-black text-sm">BLK PACK</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Ambient glow line under composition */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[70%] h-0 bg-transparent"></div>
          </div>
          
          {/* Heading + Subtitle — BELOW the video/pack */}
          <div className="text-center mt-6 mb-5">
            {/* Gold B Logo */}
            <img 
              src="/images/blk-gold-b.png" 
              alt="BLK PACKS" 
              className="w-16 h-16 mx-auto mb-4"
              data-testid="hero-gold-logo"
            />
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-3 leading-[1.08] tracking-tight">
              OWN THE MOMENTS<br />
              <span className="brand-gradient-text">THAT SHOOK THE CROWD</span>
            </h1>
            <p className="text-gray-500 text-sm md:text-base leading-relaxed max-w-lg mx-auto">
              <HighlightedText text="Official Gold line and Silver line trading cards — you collect the Moments that shook the crowd. Built different, straight premium." />
            </p>
          </div>
          
          {/* Live Reviews Ticker — animated chat-style comments */}
          <div className="mt-6 relative h-14 overflow-hidden">
            {heroReviews.map((review, idx) => (
              <div
                key={idx}
                className={`absolute inset-0 flex items-center justify-center gap-3 transition-all duration-500 ${
                  idx === activeReview 
                    ? 'opacity-100 translate-y-0' 
                    : idx === (activeReview - 1 + heroReviews.length) % heroReviews.length
                      ? 'opacity-0 -translate-y-6'
                      : 'opacity-0 translate-y-6'
                }`}
              >
                <div className="flex items-center gap-2.5 bg-white/[0.04] border border-white/[0.08] backdrop-blur-sm rounded-full px-4 py-2">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-[8px]">{review.avatar}</span>
                  </div>
                  <span className="text-amber-400 font-bold text-xs">{review.user}</span>
                  <span className="text-gray-400 text-xs">{review.text}</span>
                </div>
              </div>
            ))}
          </div>
          
          {/* Live Stats + Powered By */}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-3">
            {stats.activeDrops > 0 && (
              <div className="inline-flex items-center gap-1.5 bg-white/[0.03] border border-white/[0.06] rounded-full px-3 py-1.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                </span>
                <span className="text-white font-bold text-[10px] tracking-wider">LIVE</span>
                <span className="text-white/20 text-[10px]">/</span>
                <span className="text-amber-400 font-bold text-[10px]">{stats.available} PACKS</span>
                <span className="text-white/20 text-[10px]">/</span>
                <span className="text-white/60 text-[10px]">{stats.activeDrops} DROPS</span>
              </div>
            )}
            
            <div className="inline-flex items-center gap-1.5 bg-white/[0.03] border border-white/[0.06] px-3 py-1.5 rounded-full">
              <span className="text-white/40 text-[10px] font-medium tracking-wider uppercase">Powered by</span>
              <img 
                src="https://assets.emergent.sh/assets/Landing-Hero-E.gif" 
                alt="Emergent" 
                className="w-3.5 h-3.5 rounded-full object-cover"
              />
              <span className="text-white/70 font-bold text-[10px] tracking-wide">Emergent</span>
            </div>
          </div>
        </div>
        
        {/* Keyframe animations */}
        <style>{`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(300%); }
          }
          @keyframes heroFloat1 {
            0%, 100% { transform: rotateY(-5deg) rotateX(4deg) rotateZ(-6deg) translateY(0px); }
            50% { transform: rotateY(-5deg) rotateX(4deg) rotateZ(-6deg) translateY(-12px); }
          }
          @keyframes heroFloat2 {
            0%, 100% { transform: rotateY(-8deg) rotateX(6deg) rotateZ(-15deg) translateY(0px); }
            50% { transform: rotateY(-8deg) rotateX(6deg) rotateZ(-15deg) translateY(-8px); }
          }
          @keyframes heroFloat3 {
            0%, 100% { transform: rotateY(12deg) rotateX(-6deg) rotateZ(8deg) translateY(0px); }
            50% { transform: rotateY(12deg) rotateX(-6deg) rotateZ(8deg) translateY(-10px); }
          }
        `}</style>
      </StickyScrollSection>
      </SectionErrorBoundary>
      )}

      {/* Active Challenges Section - STICKY SCROLL */}
      {challenges.length > 0 && (
        <SectionErrorBoundary name="challenges">
        <StickyScrollSection 
          bgColor="bg-gradient-to-b from-[#0a0a0c] via-[#080808] to-[#060606]"
          className="py-12 md:py-16 px-4 relative overflow-hidden"
          zIndex={35}
        >
          {/* Background Effects - hidden on low-perf */}
          {!isLowPerf && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden hidden md:block">
            <div className="absolute top-20 right-1/4 w-[450px] h-[450px] bg-orange-500/3 rounded-full blur-[150px]"></div>
          </div>
          )}
          
          <div className="max-w-6xl mx-auto relative z-10 pt-8">
            {/* Section Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Trophy size={14} className="text-amber-400" />
                  <span className="text-amber-400 text-xs uppercase tracking-[0.2em] font-medium">Compete & Win</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-black">
                  <span className="text-white">Active </span>
                  <span className="brand-gradient-text">Challenges</span>
                </h2>
              </div>
              <button 
                onClick={() => navigate("/play")}
                className="group flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm font-medium transition-colors"
              >
                View All <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
            
            {/* Challenge Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {challenges.slice(0, 3).map((challenge) => {
                const endDate = new Date(challenge.end_date);
                const now = new Date();
                const diffMs = endDate - now;
                const daysLeft = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
                const hoursLeft = Math.max(0, Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
                
                return (
                  <div 
                    key={challenge.id} 
                    className="group relative cursor-pointer"
                    onClick={() => navigate("/play")}
                  >
                    {/* Card Glow */}
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500/20 to-amber-600/20 rounded-2xl blur-sm opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    
                    <div className="relative bg-gradient-to-br from-gray-900/90 to-[#12121e] card-spectrum card-spectrum-epic rounded-2xl overflow-hidden transition-all duration-300">
                      {/* Challenge Header */}
                      <div className="p-5 border-b border-amber-500/10">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-bold text-base group-hover:text-amber-400 transition-colors truncate">{challenge.name}</h3>
                            <p className="text-gray-500 text-sm line-clamp-1 mt-1">{challenge.description}</p>
                          </div>
                          <span className={`ml-3 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-wide ${
                            challenge.reward?.rarity === 'LEGENDARY' 
                              ? 'bg-gradient-to-r from-orange-600 to-orange-700 text-black border border-amber-400/50' 
                              : challenge.reward?.rarity === 'EPIC'
                                ? 'bg-gradient-to-r from-amber-700 to-amber-900 text-amber-100 border border-amber-400/50'
                                : challenge.reward?.rarity === 'RARE'
                                  ? 'bg-gradient-to-r from-blue-700 to-blue-900 text-blue-100 border border-blue-400/50'
                                  : 'bg-gradient-to-r from-gray-700 to-gray-800 text-gray-300 border border-gray-500/50'
                          }`}>
                            {challenge.reward?.rarity || 'REWARD'}
                          </span>
                        </div>
                        
                        {/* Time & Entry Info */}
                        <div className="flex items-center gap-4 text-xs">
                          <span className="text-amber-400 flex items-center gap-1.5">
                            <Clock size={12} />
                            {daysLeft}d {hoursLeft}h left
                          </span>
                          {challenge.entry_fee > 0 && (
                            <span className="text-amber-300 flex items-center gap-1.5">
                              <Coins size={12} />
                              {challenge.entry_fee} Pack Points
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Reward Preview */}
                      <div className="p-5 flex items-center gap-4">
                        <div className="relative">
                          <div className="absolute -inset-1 bg-amber-500/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity"></div>
                          <div className="relative w-16 h-16 rounded-xl overflow-hidden bg-amber-500/10 border border-amber-500/20 flex-shrink-0">
                            {challenge.reward?.image_url ? (
                              <img src={getMediaUrl(challenge.reward.image_url)} alt="Reward" className="w-full h-full object-contain" onError={(e) => { e.target.style.display = "none"; }} />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Gift className="text-amber-400" size={28} />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-500 text-[10px] uppercase tracking-wider font-medium">Reward</p>
                          <p className="text-white text-sm font-bold truncate mt-0.5">{challenge.reward?.name || 'Exclusive Collectible'}</p>
                          <p className="text-gray-500 text-xs mt-0.5">{challenge.required_moments?.length || 0} moments required</p>
                        </div>
                        <ChevronRight className="text-amber-400 group-hover:translate-x-1 transition-transform" size={22} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* CTA Button */}
            <div className="text-center mt-10 pb-6">
              <button 
                onClick={() => navigate("/play")}
                className="group relative inline-flex items-center gap-2"
              >
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 to-amber-600/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative bg-amber-500/10 border border-amber-500/30 hover:border-amber-500/50 rounded-full px-8 py-3 text-amber-400 font-bold text-sm transition-all flex items-center gap-2">
                  <Trophy size={16} />
                  All Challenges
                </div>
              </button>
            </div>
          </div>
        </StickyScrollSection>
        </SectionErrorBoundary>
      )}


      {/* EXPLORE THE SWAP SPOT Section - STICKY SCROLL */}
      <SectionErrorBoundary name="swap-spot">
      <StickyScrollSection 
        bgColor="bg-gradient-to-b from-[#0a0a0c] via-[#080808] to-[#060606]"
        className="pt-12 md:pt-16 pb-6 px-4 relative overflow-hidden"
        zIndex={55}
      >
        {/* Background - hidden on low-perf */}
        {!isLowPerf && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden hidden md:block">
          <div className="absolute top-20 left-1/4 w-[400px] h-[400px] bg-white/[0.015] rounded-full blur-[150px]"></div>
        </div>
        )}
        
        <div className="max-w-6xl mx-auto relative z-10">
          {/* Header */}
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-5xl font-black text-white mb-4">
              EXPLORE THE <span className="brand-gradient-text">SWAP SPOT</span>
            </h2>
            <p className="text-gray-400 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              <HighlightedText text="Stay locked in with real-time market vibes and the full story behind every Moment. Every Trade, Mint, and Pack Rip is indexed on EMERGE." />
            </p>
          </div>
          
          {/* Stats Row with Time Filter */}
          <div className="mb-12">
            {/* Time Period Filter */}
            <div className="flex justify-end mb-4">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-500">TIME PERIOD:</span>
                {['1D', '7D', '30D', '60D', '90D'].map((period) => (
                  <button 
                    key={period}
                    onClick={() => handleSwapSpotPeriodChange(period)}
                    className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
                      swapSpotPeriod === period
                        ? 'bg-white text-black' 
                        : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50'
                    }`}
                  >
                    {period}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-black/60 border border-orange-500/30 hover:border-orange-500/60 rounded-xl p-4 transition-colors">
                <div className="text-orange-400/80 text-xs mb-1 font-bold tracking-wider">SALES VOLUME</div>
                <div className="text-white text-xl md:text-2xl font-black">${(swapSpotStats.sales_volume || 0).toLocaleString()}</div>
              </div>
              <div className="bg-black/60 border border-orange-500/30 hover:border-orange-500/60 rounded-xl p-4 transition-colors">
                <div className="text-orange-400/80 text-xs mb-1 font-bold tracking-wider">PURCHASES</div>
                <div className="text-white text-xl md:text-2xl font-black">{(swapSpotStats.purchases || 0).toLocaleString()}</div>
              </div>
              <div className="bg-black/60 border border-orange-500/30 hover:border-orange-500/60 rounded-xl p-4 transition-colors">
                <div className="text-orange-400/80 text-xs mb-1 font-bold tracking-wider">BUYERS</div>
                <div className="text-white text-xl md:text-2xl font-black">{(swapSpotStats.buyers || 0).toLocaleString()}</div>
              </div>
              <div className="bg-black/60 border border-orange-500/30 hover:border-orange-500/60 rounded-xl p-4 transition-colors">
                <div className="text-orange-400/80 text-xs mb-1 font-bold tracking-wider">SELLERS</div>
                <div className="text-white text-xl md:text-2xl font-black">{(swapSpotStats.sellers || 0).toLocaleString()}</div>
              </div>
              <div className="bg-black/60 border border-red-500/40 hover:border-red-500/70 rounded-xl p-4 col-span-2 md:col-span-1 transition-colors">
                <div className="text-orange-400/80 text-xs mb-1 font-bold tracking-wider">BIG PURCHASES <span className="text-gray-600 font-normal">($400+)</span></div>
                <div className="text-white text-xl md:text-2xl font-black">{(swapSpotStats.big_purchases || 0).toLocaleString()}</div>
              </div>
            </div>
          </div>
          
          {/* TRENDING MOMENTS */}
          <div className="mb-10">
            <h3 className="text-2xl md:text-3xl font-black mb-2"><span className="brand-gradient-text">TRENDING</span> MOMENTS</h3>
            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
              <HighlightedText text="The hottest Moments right now based on volume over the last 7 days. Every Rare, Epic, and Legendary pull trending across the ecosystem." />
            </p>
            
            <div className="flex gap-3 snap-slider pb-4">
              {trendingMoments.length > 0 ? trendingMoments.map((item, i) => (
                <div key={item.id || i} className="w-[160px] flex-shrink-0 flex flex-col">
                  <BLKCardFrame 
                    imageUrl={item.image_url || item.home_display_image}
                    videoUrl={item.video_url}
                    name={item.name}
                    rarity={item.rarity}
                    isHobby={!!item.is_hobby}
                    serial={item.edition_number}
                    series={item.series || item.set_name}
                    isAR={item.is_ar}
                    isHot
                    badge={<span className="bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">#{i + 1}</span>}
                    onClick={() => navigate('/marketplace')}
                    verificationId={item.verification_id}
                  />
                  <div className="mt-1.5 px-0.5">
                    <p className="text-white text-xs font-bold truncate">{item.name}</p>
                    <p className="text-amber-300 text-sm font-black">{item.list_price?.toFixed(0) || 0} pts</p>
                  </div>
                </div>
              )) : !dataLoaded ? (
                // Loading skeletons — only show while API is still in-flight.
                // Previously this branch ran for BOTH loading AND empty states,
                // which made an empty production marketplace look like a
                // broken loading screen on iOS (bug 2026-06-09).
                <>
                  {[1,2,3,4].map(i => (
                    <div key={i} className="min-w-[200px] h-[280px] bg-gray-800/30 rounded-xl animate-pulse flex-shrink-0"></div>
                  ))}
                </>
              ) : (
                // Genuine empty state — API responded with zero trending items.
                // Show a clear, non-misleading empty card with a CTA into the
                // Drops feed where the user CAN take action (the homepage above
                // this section already lists upcoming drops).
                <div className="w-full bg-gradient-to-br from-orange-500/5 to-amber-500/5 border border-orange-500/20 rounded-2xl px-5 py-8 text-center" data-testid="trending-moments-empty">
                  <p className="text-white font-black text-lg mb-1">No moments trending yet</p>
                  <p className="text-white/60 text-xs mb-4">Once packs start ripping, the hottest pulls land here.</p>
                  <button
                    type="button"
                    onClick={() => navigate('/drops')}
                    className="brand-gradient-btn font-black px-5 py-2.5 rounded-xl text-sm"
                    data-testid="trending-moments-empty-cta"
                  >
                    Browse Drops →
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* TOP PURCHASES */}
          <div className="mb-10">
            <h3 className="text-2xl md:text-3xl font-black mb-2"><span className="brand-gradient-text">TOP</span> PURCHASES</h3>
            <p className="text-gray-500 text-sm mb-6">
              <HighlightedText text="Tap in with collectors making big moves — your next legendary Moment could be yours." />
            </p>
            
            <div className="flex gap-3 snap-slider pb-4">
              {topPurchases.length > 0 ? topPurchases.map((item, i) => (
                <div key={item.id || i} className="w-[160px] flex-shrink-0 flex flex-col">
                  <BLKCardFrame 
                    imageUrl={item.image_url || item.home_display_image}
                    videoUrl={item.video_url}
                    name={item.name}
                    rarity={item.rarity}
                    isHobby={!!item.is_hobby}
                    serial={item.edition_number}
                    series={item.series || item.set_name}
                    isAR={item.is_ar}
                    isHot
                    badge={<span className="bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">BIG MOVE</span>}
                    onClick={() => navigate('/marketplace')}
                    verificationId={item.verification_id}
                  />
                  <div className="mt-1.5 px-0.5">
                    <p className="text-white text-xs font-bold truncate">{item.name}</p>
                    <p className="text-amber-300 text-sm font-black">{item.list_price?.toFixed(0) || item.price?.toFixed(0) || 0} pts</p>
                  </div>
                </div>
              )) : !dataLoaded ? (
                <>
                  {[1,2,3,4].map(i => (
                    <div key={i} className="min-w-[200px] h-[280px] bg-gray-800/30 rounded-xl animate-pulse flex-shrink-0"></div>
                  ))}
                </>
              ) : (
                <div className="w-full bg-gradient-to-br from-orange-500/5 to-amber-500/5 border border-orange-500/20 rounded-2xl px-5 py-8 text-center" data-testid="top-purchases-empty">
                  <p className="text-white font-black text-lg mb-1">No big moves yet</p>
                  <p className="text-white/60 text-xs mb-4">As collectors start swapping, the biggest buys show up here.</p>
                  <button
                    type="button"
                    onClick={() => navigate('/drops')}
                    className="brand-gradient-btn font-black px-5 py-2.5 rounded-xl text-sm"
                    data-testid="top-purchases-empty-cta"
                  >
                    See Live Drops →
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* RECENT PURCHASES */}
          <div className="mb-10">
            <h3 className="text-2xl md:text-3xl font-black mb-2"><span className="brand-gradient-text">RECENT</span> PURCHASES</h3>
            <p className="text-gray-500 text-sm mb-6">
              <HighlightedText text="Legendary Moments don't sit long. Lock yours in." />
            </p>
            
            <div className="flex gap-3 snap-slider pb-4">
              {recentPurchases.length > 0 ? recentPurchases.map((item, i) => (
                <div key={item.id || i} className="w-[160px] flex-shrink-0 flex flex-col">
                  <BLKCardFrame 
                    imageUrl={item.image_url || item.home_display_image}
                    videoUrl={item.video_url}
                    name={item.name}
                    rarity={item.rarity}
                    isHobby={!!item.is_hobby}
                    serial={item.edition_number}
                    series={item.series || item.set_name}
                    isAR={item.is_ar}
                    onClick={() => navigate('/marketplace')}
                    verificationId={item.verification_id}
                  />
                  <div className="mt-1.5 px-0.5">
                    <p className="text-white text-xs font-bold truncate">{item.name}</p>
                    <p className="text-amber-300 text-sm font-black">{item.list_price?.toFixed(0) || item.price?.toFixed(0) || 0} pts</p>
                  </div>
                </div>
              )) : !dataLoaded ? (
                <>
                  {[1,2,3,4].map(i => (
                    <div key={i} className="min-w-[200px] h-[280px] bg-gray-800/30 rounded-xl animate-pulse flex-shrink-0"></div>
                  ))}
                </>
              ) : (
                <div className="w-full bg-gradient-to-br from-orange-500/5 to-amber-500/5 border border-orange-500/20 rounded-2xl px-5 py-8 text-center" data-testid="recent-purchases-empty">
                  <p className="text-white font-black text-lg mb-1">No recent purchases yet</p>
                  <p className="text-white/60 text-xs mb-4">Be the first to lock in a Moment.</p>
                  <button
                    type="button"
                    onClick={() => navigate('/marketplace')}
                    className="brand-gradient-btn font-black px-5 py-2.5 rounded-xl text-sm"
                    data-testid="recent-purchases-empty-cta"
                  >
                    Shop Swaps →
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Shop Button */}
          <div className="text-center pb-6">
            <button 
              onClick={() => navigate('/marketplace')}
              className="px-8 py-3.5 brand-gradient-btn font-black text-sm rounded-xl shadow-lg shadow-orange-500/30"
            >
              SHOP SWAPS
            </button>
          </div>
        </div>
      </StickyScrollSection>
      </SectionErrorBoundary>
      
      {/* Ready to Start Collecting CTA - FINAL STICKY SCROLL */}
      <StickyScrollSection 
        bgColor="bg-gradient-to-b from-black via-[#06060e] to-[#0a0a16]"
        className="pt-2 md:pt-4 px-4 pb-6"
        zIndex={75}
        isLast={true}
      >
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection animation="fade-up">
            <h2 className="text-2xl md:text-3xl font-black text-white mb-3">Ready to <span className="brand-gradient-text">Start Collecting</span>?</h2>
            <p className="text-gray-400 text-base mb-6">
              <HighlightedText text="Join thousands of collectors building their digital lockers" />
            </p>
            
            {!user ? (
              <button 
                onClick={() => navigate("/register")}
                className="px-8 py-3.5 brand-gradient-btn font-black text-sm rounded-xl shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50"
                data-testid="create-free-account-btn"
              >
                SIGN UP FREE
              </button>
            ) : (
              <button 
                onClick={() => navigate("/drops")}
                className="px-8 py-3.5 brand-gradient-btn font-black text-sm rounded-xl shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50"
                data-testid="explore-drops-btn"
              >
                BROWSE DROPS
              </button>
            )}
          </AnimatedSection>
        </div>
      </StickyScrollSection>

      {/* Graded Note Popup Modal */}
      {showGradedNotePopup && (
        <div 
          className="fixed inset-0 bg-[#12121e]/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowGradedNotePopup(false)}
        >
          <div 
            className="relative max-w-lg w-full animate-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button 
              onClick={() => setShowGradedNotePopup(false)}
              className="absolute -top-12 right-0 text-gray-400 hover:text-white transition-colors flex items-center gap-2"
            >
              <span className="text-sm">Close</span>
              <span className="text-xl">&times;</span>
            </button>
            
            {/* Graded Badge */}
            <div className="absolute -top-4 right-8 z-10 bg-gradient-to-r from-orange-500 to-amber-500 text-black text-sm font-black px-5 py-2 rounded-full shadow-lg shadow-orange-500/40">
              GRADED 10
            </div>
            
            {/* Large Note Display */}
            <div className="aspect-[2/3] rounded-3xl overflow-hidden border-4 border-orange-500/70 bg-gradient-to-br from-gray-800 via-[#0d0d15] to-[#12121e] shadow-[0_0_100px_rgba(249,115,22,0.3)]">
              {(() => {
                const drop = featuredDrop || drops[0];
                const noteImages = drop?.graded_note_images?.filter(img => img && img.trim() !== '') || [];
                const singleNote = drop?.graded_note_image_url;
                if (noteImages.length > 0) {
                  const currentIdx = gradedNoteIndex % noteImages.length;
                  return (
                    <div className="relative w-full h-full">
                      <ResponsiveImage
                        key={currentIdx}
                        src={getMediaUrl(noteImages[currentIdx])}
                        alt={`Graded Note ${currentIdx + 1}`}
                        className="w-full h-full object-contain p-6 transition-opacity duration-700"
                        sizes="(max-width: 768px) 100vw, 600px"
                      />
                      {noteImages.length > 1 && (
                        <>
                          <button
                            onClick={() => setGradedNoteIndex(prev => (prev - 1 + noteImages.length) % noteImages.length)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
                          >
                            &lt;
                          </button>
                          <button
                            onClick={() => setGradedNoteIndex(prev => (prev + 1) % noteImages.length)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
                          >
                            &gt;
                          </button>
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                            {noteImages.map((_, idx) => (
                              <button
                                key={idx}
                                onClick={() => setGradedNoteIndex(idx)}
                                className={`w-2.5 h-2.5 rounded-full transition-all ${idx === currentIdx ? 'bg-amber-400 w-5' : 'bg-gray-500/60'}`}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                } else if (singleNote) {
                  return (
                    <ResponsiveImage
                      src={getMediaUrl(singleNote)}
                      alt="Graded Note"
                      className="w-full h-full object-contain p-6"
                      sizes="(max-width: 768px) 100vw, 600px"
                    />
                  );
                } else {
                  return (
                    <div className="w-full h-full flex flex-col items-center justify-center p-8">
                      <Star className="text-amber-400 mb-4" size={64} fill="currentColor" />
                      <span className="text-amber-400 text-2xl font-bold tracking-wider">GRADED</span>
                      <span className="text-white text-8xl font-black">70</span>
                      <span className="text-gray-400 text-lg mt-4">Legendary / Epic Tier</span>
                    </div>
                  );
                }
              })()}
            </div>
            
            {/* Label */}
            <div className="mt-6 text-center">
              <p className="text-amber-400 font-bold text-xl">Officially Graded</p>
              <p className="text-gray-400 text-sm mt-1">Legendary / Epic Tier • Professionally Authenticated</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Push Notification Prompt - Shows for logged in users who haven't enabled push */}
      {user && token && (
        <PushNotificationPrompt token={token} />
      )}

      {/* AR Viewer Overlay - removed, AR only available through Locker */}
    </div>
  );
};

// Export with AuthContext
export default HomePage;

